import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import appConfig from 'appConfig';

import { Button, Input, Select, Checkbox } from '@pearsontechnology/aero-components';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { commandWorkspaceProjectIngestList } from 'containers/ConnectionManager/actions';
import Modal, { ModalActions } from 'components/Modal';
import projectServiceShape from 'services/types/projectService';
import workspaceService from 'services/WorkspaceService';
import { featureKeys } from 'services/FeatureFlagService';
import enhanceWithServices from 'hocs/enhanceWithServices';
import { FileTypes, FileMimeTypes } from 'constants/enums';
import messages from './messages';
import {
  HTTP_ERROR_CODE_DUPLICATE_COURSENAME,
  UPLOAD_PROGRESS,
  UPLOAD_COMPLETED,
  UPLOAD_FAILED,
  COURSE_FORMAT_LIST_EPUB,
  COURSE_FORMAT_LIST_WORD_BV,
  COURSE_FORMAT_LIST_WORD,
  COURSE_FORMAT_LIST_AMBROSIA,
} from "./constants";
import {
  ModalContent,
  Spacer,
  InputWithoutErrorWarningIcon,
  ImportFileDropZoneLabel,
} from './styles';
import UploadFileModal from './UploadFileModal';
import Import from './ImportAssetUploadLifeCycle';
import ImportFileDropZone from '../ImportFileDropZone';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';
import { gtmTrackAuthoringCourseContentUserInteraction } from 'utils/gtm/handler';
import { GTM_EVENT_CATEGORY, GTM_EVENT_ACTION } from 'utils/gtm/constants';
const ImportModal = ({
  projectId,
  projectService,
  visible,
  onDismiss,
  selectImportHistoryTab = () => { }, // added default empty method to avoid error while calling ImportModal in courseLevel.
  getProjectIngestList,
  isBVCoursePlugin = false,
  rootElementId,
  fileName,
  ingestionType = 'course',
  getFeatureFlagValue,
}) => {
  const isStudyChannelFFEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_IMPORT_STUDYCHANNEL);
  const isAmbrosiaIngestFFEnabled = getFeatureFlagValue(featureKeys.INGESTION_AMBROSIA_FORMAT_OPTION);
  const isEpubTypeFFEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_BV_EPUB_IMPORT);
  const isWordImportBVEnabled = getFeatureFlagValue(featureKeys.BRNT_22755_BRONTE_VIEWER_WORD_IMPORT);
  const isSlateJSGuardrailEnabled = getFeatureFlagValue(featureKeys.BVEW_1232_Slate_JS_Guardrail);
  
  const COURSE_FORMAT_LIST = {
    ...COURSE_FORMAT_LIST_EPUB,
    ...(isWordImportBVEnabled && COURSE_FORMAT_LIST_WORD_BV),
    ...(isStudyChannelFFEnabled && COURSE_FORMAT_LIST_WORD),
    ...(isAmbrosiaIngestFFEnabled && COURSE_FORMAT_LIST_AMBROSIA),
  };
  const [isLoading, setIsLoading] = useState(false);
  const [staticBackdrop, setStaticBackdrop] = useState(false);
  const [responseErrorMessage, setResponseErrorMessage] = useState('');
  const [percentage, setPercentage] = useState(0);
  const [uploadState, setUploadState] = useState();
  const [importEnabled, setImportEnabled] = useState(false);
  const [courseFormat, setCourseFormat] = useState(COURSE_FORMAT_LIST.EPUB_ETEXT);
  const [supportedFileTypes, setSupportedFileTypes] = useState(ingestionType === 'chapter' ? [FileTypes.DOCX] : [FileTypes.EPUB]);
  const fileTypeAndIngestionType = `${supportedFileTypes.reduce((accumulator, value) => `${accumulator}_${value}`, '')}_${ingestionType}`;
  const [isUseScreenWithUpdatedSlate, setIsUseScreenWithUpdatedSlate] = useState(false);

  const uploadData = useRef();

  const validateFields = ({ courseName, file }) => {
    const errors = {};
    let validFields = true;
    if (!courseName) {
      validFields = false;
    } else if (responseErrorMessage) {
      // Reset the previous response
      setResponseErrorMessage('');
    }
    if (!file) {
      validFields = false;
    }
    if (validFields) {
      setImportEnabled(true);
    } else {
      setImportEnabled(false);
    }
    return errors;
  };

  const handleImport = async ({ courseName, file }) => {
    setIsLoading(true);
    let configFields;
    if (courseFormat === COURSE_FORMAT_LIST.EPUB_BRONTE_VIEWER ||
      courseFormat === COURSE_FORMAT_LIST.WORD_BVPLUGIN ||
      isBVCoursePlugin) {
      configFields = JSON.stringify([{
        course: appConfig.getEpubBronteViewPluginId(),
        lesson: appConfig.getEpubBronteViewLessonId(),
        unit: appConfig.getEpubBronteViewUnitId(),
        screen: appConfig.getEpubBronteViewScreenId(),
        isCoverUnitRestricted: appConfig.getRestrictedCoverUnitForBV(),
      }]);
    }  else if (courseFormat === COURSE_FORMAT_LIST.BRONTE_VIEWER_STITCHED_LESSON) {
      configFields = JSON.stringify([{
        course: appConfig.getStitchedLessonBronteViewCoursePluginId(),
        unit: appConfig.getStitchedLessonBronteViewUnitId(),
        lesson: appConfig.getStitchedLessonBronteViewLessonId(),
        screen: appConfig.getStitchedLessonBronteViewScreenId(),
      }]);
    } else if (courseFormat === COURSE_FORMAT_LIST.WORD_STUDY_CHANNEL) {
      configFields = JSON.stringify([{
        course: appConfig.getStudyChannelViewId(),
        lesson: appConfig.getStudyChannelViewLessonId(),
        screen: appConfig.getStudyChannelViewScreenId(),
      }]);
    } else if (courseFormat === COURSE_FORMAT_LIST.AMBROSIA_IMPORT) {
      //course key not needed because it only overrides and ambrosia doesn't need an override
      configFields = JSON.stringify([
        {
          "optional-id": false,
          "useScreenWithUpdatedSlate": isUseScreenWithUpdatedSlate,
        },
      ]);
    } else {
      configFields = JSON.stringify([{ course: appConfig.getEpubCoursePluginId() }]);
    }

    const workspaceId = workspaceService.getSelectedWorkspaceId();

    let fileType;

    Object.entries(FileMimeTypes).some((([fileTypeName, mimeType]) => {
      if (mimeType.includes(file.type)) {
        fileType = fileTypeName;
        return true;
      }
      return false;
    }));

    if (!fileName) {
      if (fileType) {
        if (fileType === FileTypes.AMBROSIA) {
          fileName = 'ambrosia.json';
        } else {
          fileName = `content.${FileTypes[fileType].toLowerCase()}`;
        }
      } else {
        fileName = 'content.epub';
      }
    }

    const response = await projectService.requestIngestion(
      projectId,
      configFields,
      fileName,
      courseName,
      workspaceId,
      rootElementId,
    );
    if (response?.error || !response) {
      setIsLoading(false);
      const message = getResponseErrorMesage(response?.error);
      setResponseErrorMessage(message);
    } else {
      const { url, ingestionId } = response;
      uploadData.current = {
        file,
        url,
        ingestionId,
        courseName,
      };
      Import.init({
        projectId,
        ingestionId,
        adapterType: supportedFileTypes.find((type) => type === fileType),
        setPercentage,
        setUploadState,
        setIsLoading,
        onDismiss,
        getProjectIngestList,
        selectImportHistoryTab,
      });
      Import.upload({ file, url });
      setUploadState(UPLOAD_PROGRESS);
      setStaticBackdrop(true);
    }
  };

  const getResponseErrorMesage = ({ code, message }) => {
    if (code === HTTP_ERROR_CODE_DUPLICATE_COURSENAME) {
      return intl.formatMessage(messages.duplicateCourseNameError);
    }
    if (message) {
      return `${message[0].toUpperCase()}${message.slice(1)}`;
    }
    return intl.formatMessage(messages.unexpectedError);
  };

  const handleUploadCancel = async () => {
    Import.cancel();
  };

  const handleClose = () => {
    if (uploadState === UPLOAD_PROGRESS) {
      handleUploadCancel();
    }
    // Ensuring the current ingestion is deleted,
    // for network failed scenarios
    if (uploadState === UPLOAD_FAILED) {
      Import.delete();
    }
    onDismiss();
  };

  const handleTryAgain = () => {
    const { file, courseName } = uploadData.current;
    // Ensuring the current ingestion is deleted,
    // for network failed scenarios
    Import.delete();
    setUploadState(UPLOAD_PROGRESS);
    handleImport({ courseName, file });
  };

  const handleSelectChange = (event) => {
    // Event ID 20 — Select Import Format
    gtmTrackAuthoringCourseContentUserInteraction({
      eventCategory: GTM_EVENT_CATEGORY.COURSE_PROJECT_DASHBOARD,
      eventAction: GTM_EVENT_ACTION.SELECT_IMPORT_FORMAT_FROM_DROPDOWN,
      eventLabel: intl.formatMessage(messages[event.target.value]),
      locationInApp: 'Select a Project in Courseware > Click on Add Content Dropdown > Select Import Content Type > Select Import Format From Dropdown',
    });
    setCourseFormat(event.target.value);

    switch (event.target.value) {
      case COURSE_FORMAT_LIST.WORD_ETEXT:
      case COURSE_FORMAT_LIST.WORD_STUDY_CHANNEL:
      case COURSE_FORMAT_LIST.WORD_BVPLUGIN:
        setSupportedFileTypes([FileTypes.DOCX]);
        break;
      case COURSE_FORMAT_LIST.AMBROSIA_IMPORT:
        setSupportedFileTypes([FileTypes.AMBROSIA]);
        break;
      default:
        setSupportedFileTypes([FileTypes.EPUB]);
        break;
    }
  };

  const handleCompleteImportClick = () => {
    const selectedFormatLabel = intl.formatMessage(messages[courseFormat]);
    // Event ID 21 — Complete Content Import
    gtmTrackAuthoringCourseContentUserInteraction({
      eventCategory: GTM_EVENT_CATEGORY.COURSE_PROJECT_DASHBOARD,
      eventAction: GTM_EVENT_ACTION.CLICK_TO_COMPLETE_IMPORT,
      eventLabel: selectedFormatLabel,
      locationInApp: 'Select a Project in Courseware > Click on Add Content Dropdown > Select Import Content Type > Select Import Format, give a name for the course and select a file to import > Click To Complete Import',
    });
  };

  const startIngestionModal = () => (
    <Formik
      onSubmit={({ courseName, file }) => {
        handleImport({
          courseName: courseName.trim(),
          file,
        });
      }}
      validate={validateFields}
      validateOnChange
      validateOnBlur
      initialValues={{ courseName: '', file: undefined }}
    >
      <Form>
        <ModalContent>
          <Field name="courseName">
            {({ field, form }) => (
              <InputWithoutErrorWarningIcon>
                <Input
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  label={intl.formatMessage(messages.courseName)}
                  placeholder={intl.formatMessage(
                    messages[`namePlaceholder${fileTypeAndIngestionType}`]
                  )}
                  feedback={`${form.errors[field.name] ||
                    responseErrorMessage}`}
                  disabled={isLoading}
                />
              </InputWithoutErrorWarningIcon>
            )}
          </Field>
          <Spacer />
          {ingestionType === 'course' && isEpubTypeFFEnabled && (
            <>
              <Select
                onChange={handleSelectChange}
                label={intl.formatMessage(messages.courseFormat)}
                disabled={isLoading}
              >
                {Object.keys(COURSE_FORMAT_LIST).map((format) => (<option
                  key={format}
                  value={COURSE_FORMAT_LIST[format]}
                >
                  {intl.formatMessage(messages[format])}
                </option>))}
              </Select>
              <Spacer />
            </>
          )}
          {
            ingestionType === 'course' && supportedFileTypes.includes(FileTypes.AMBROSIA) && isSlateJSGuardrailEnabled && (
              <>
                <Checkbox
                  label={intl.formatMessage(messages.useNewScreenPlugin)}
                  checked={isUseScreenWithUpdatedSlate}
                  onChange={() => setIsUseScreenWithUpdatedSlate(!isUseScreenWithUpdatedSlate)}
                />
                <Spacer />
              </>
            )}
          <Field name="file">
            {({ field, form }) => (
              <>
                <ImportFileDropZoneLabel>
                  {intl.formatMessage(messages.importFile)}
                </ImportFileDropZoneLabel>
                <ImportFileDropZone
                  error={form.errors[field.name]}
                  disabled={isLoading}
                  setFieldValue={form.setFieldValue}
                  fieldName="file"
                  supportType={supportedFileTypes}
                />
              </>
            )}
          </Field>
        </ModalContent>
        <ModalActions>
          <Button grey onClick={onDismiss}>
            {intl.formatMessage(messages.cancel)}
          </Button>
          <Button
            disabled={!importEnabled}
            primary
            loadable
            isLoading={isLoading}
            type="submit"
            onClick={handleCompleteImportClick}
          >
            {intl.formatMessage(messages.import)}
          </Button>
        </ModalActions>
      </Form>
    </Formik>
  );

  return (
    <Modal
      closeOnOuterClick={!staticBackdrop}
      showCloseIcon={!(uploadState === UPLOAD_COMPLETED)}
      onDismiss={handleClose}
      visible={visible}
      title={
        !uploadState
          ? intl.formatMessage(messages[`headerInfo${fileTypeAndIngestionType}`])
          : intl.formatMessage(messages.uploadFile)
      }
    >
      {!uploadState ? (
        startIngestionModal()
      ) : (
        <UploadFileModal
          percentage={percentage}
          file={uploadData.current.file}
          uploadState={uploadState}
          handleTryAgain={handleTryAgain}
          handleClose={handleClose}
        ></UploadFileModal>
      )}
    </Modal>
  );
};

function mapDispatchToProps(dispatch) {
  return {
    getProjectIngestList: (projectId) =>
      dispatch(commandWorkspaceProjectIngestList(projectId)),
  };
}

ImportModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  projectService: projectServiceShape,
  selectImportHistoryTab: PropTypes.func,
  getProjectIngestList: PropTypes.func.isRequired,
  isBVCoursePlugin: PropTypes.bool,
  fileName: PropTypes.string,
  rootElementId: PropTypes.string,
};

const flagNames = [featureKeys.WORKSPACE_IMPORT_STUDYCHANNEL,featureKeys.INGESTION_AMBROSIA_FORMAT_OPTION, featureKeys.WORKSPACE_BV_EPUB_IMPORT];
export default compose(
  connect(null, mapDispatchToProps),
  enhanceWithServices, enhanceWithFeatureFlagSubscription
)(ImportModal, flagNames);
